/**
 * Forgot Password Screen
 * Screen to request a password reset link via email
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { authApi } from '../../api/endpoints/auth';
import { validateEmail } from '../../utils/validators';
import Button from '../common/Button';
import Input from '../common/Input';
import ErrorMessage from '../common/ErrorMessage';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import logger from '../../utils/logger';

interface ForgotPasswordScreenProps {
  navigation?: any;
}

export default function ForgotPasswordScreen({ navigation }: ForgotPasswordScreenProps) {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!email.trim()) {
      newErrors.email = 'Email requis';
    } else {
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        newErrors.email = emailValidation.error || 'Email invalide';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRequestReset = async () => {
    setApiError(null);
    setErrors({});

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.requestPasswordReset(email.trim().toLowerCase());

      if (response.success) {
        setEmailSent(true);
      }
    } catch (error: any) {
      logger.error('Request password reset error:', error);
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        const formattedErrors = Object.entries(apiErrors)
          .map(([field, messages]) => {
            const messageList = Array.isArray(messages) ? messages : [messages];
            return messageList.map(msg => `${field}: ${msg}`).join('\n');
          })
          .join('\n');
        setApiError(formattedErrors);
      } else {
        setApiError('Erreur lors de l\'envoi. Veuillez reessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Success state - email sent
  if (emailSent) {
    return (
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <View style={styles.successIcon}>
              <Text style={styles.successIconText}>✉️</Text>
            </View>

            <Text style={styles.title}>Email envoye !</Text>
            <Text style={styles.subtitle}>
              Si un compte existe avec l'adresse {email}, vous recevrez un lien pour reinitialiser votre mot de passe.
            </Text>
            <Text style={styles.hint}>
              Pensez a verifier vos spams si vous ne voyez pas l'email.
            </Text>

            <View style={styles.form}>
              <Button
                title="Retour a la connexion"
                onPress={() => navigation?.navigate('Login')}
                fullWidth
                style={styles.button}
              />

              <Button
                title="Renvoyer l'email"
                onPress={() => {
                  setEmailSent(false);
                  handleRequestReset();
                }}
                variant="outline"
                disabled={loading}
                style={styles.secondaryButton}
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  // Initial state - email input form
  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.content}>
          <Text style={styles.title}>Mot de passe oublie ?</Text>
          <Text style={styles.subtitle}>
            Entrez votre adresse email pour recevoir un lien de reinitialisation
          </Text>

          <View style={styles.form}>
            <Input
              label="Email"
              value={email}
              onChangeText={setEmail}
              placeholder="votre@email.com"
              autoCapitalize="none"
              keyboardType="email-address"
              autoComplete="email"
              error={errors.email}
              editable={!loading}
            />

            <ErrorMessage error={apiError} />

            <Button
              title="Envoyer le lien"
              onPress={handleRequestReset}
              loading={loading}
              disabled={loading}
              fullWidth
              style={styles.button}
            />

            <Button
              title="Retour a la connexion"
              onPress={() => navigation?.navigate('Login')}
              variant="text"
              disabled={loading}
              style={styles.secondaryButton}
            />
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: spacing.xl,
  },
  content: {
    padding: spacing.xl,
  },
  successIcon: {
    alignSelf: 'center',
    marginBottom: spacing.lg,
  },
  successIconText: {
    fontSize: 64,
  },
  title: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  hint: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  form: {
    marginTop: spacing.xl,
  },
  button: {
    marginTop: spacing.md,
  },
  secondaryButton: {
    marginTop: spacing.sm,
  },
});
